/**
 * The media-plan reference data (units, categories, agencies) and the
 * advertiser registry, loaded once for the whole Sales dashboard and shared
 * by the Media Plans and Rate Cards tabs — so an advertiser edited under
 * Rate Cards is the one the plan builder offers next.
 *
 * Nothing loads until a tab asks (`enabled`). A failed reload keeps the data
 * already shown and reports the error beside it.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { getMediaPlanReference, listAdvertisers } from '../../../services/mediaPlansApi';
import type { AdvertiserRecord, MediaPlanReference } from './types';

export interface MediaPlanRegistry {
  reference: MediaPlanReference | null;
  advertisers: AdvertiserRecord[];
  loading: boolean;
  error: string | null;
  reload: () => void;
  /** Add or replace one advertiser (after a create or an edit). */
  upsertAdvertiser: (record: AdvertiserRecord) => void;
}

const errorMessage = (err: unknown): string => (err instanceof Error ? err.message : 'Something went wrong.');

export function useMediaPlanRegistry(enabled: boolean): MediaPlanRegistry {
  const [reference, setReference] = useState<MediaPlanReference | null>(null);
  const [advertisers, setAdvertisers] = useState<AdvertiserRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const requested = useRef(false);
  const generation = useRef(0);
  const mounted = useRef(true);
  // Set on every mount: StrictMode runs this cleanup once on its simulated unmount in development.
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    const gen = ++generation.current;
    setLoading(true);
    setError(null);
    try {
      const [ref, advs] = await Promise.all([getMediaPlanReference(), listAdvertisers()]);
      if (!mounted.current || gen !== generation.current) return;
      setReference(ref);
      setAdvertisers(advs);
    } catch (err) {
      if (mounted.current && gen === generation.current) setError(errorMessage(err));
    } finally {
      if (mounted.current && gen === generation.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!enabled || requested.current) return;
    requested.current = true;
    void load();
  }, [enabled, load]);

  const reload = useCallback(() => {
    requested.current = true;
    void load();
  }, [load]);

  const upsertAdvertiser = useCallback((record: AdvertiserRecord) => {
    setAdvertisers((prev) => (prev.some((a) => a.key === record.key) ? prev.map((a) => (a.key === record.key ? record : a)) : [...prev, record]));
  }, []);

  return useMemo(
    () => ({ reference, advertisers, loading, error, reload, upsertAdvertiser }),
    [reference, advertisers, loading, error, reload, upsertAdvertiser],
  );
}
